import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deletePointCategoryId, getBranchCategories } from '../../../../api/point';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../../../utils/methods';
import CategoryEditModal from '../modal/CategoryEditModal';
import AlertModal from '../../../common/modal/AlertModal';
import ConfirmModal from '../../../common/modal/ConfirmModal';

// Styled Components
const CategoryListContainer = styled(Box)({
  width: '320px',
  borderRadius: '5px',
  height: 'auto',
  borderBottom: '0',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const CategoryItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '48px',
  padding: '0 10px',
  border: isSelected ? '2px solid #4AB3BC' : '1px solid #D9D9D9',
  borderRadius: '8px',
  cursor: 'pointer',
  backgroundColor: isSelected ? '#f0fafa' : 'transparent',
}));

const CategoryName = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  fontWeight: '500',
  fontSize: '16px',
  color: isSelected ? '#4AB3BC' : '#333',
}));

const EditButton = styled(Button)({
  border: '1px solid #D9D9D9',
  height: '30px',
  borderRadius: '5px',
  color: '#55C982',
  fontSize: '16px',
});

const AddCategoryButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '12px 0',
  cursor: 'pointer',
  color: '#4AB3BC',
  fontSize: '18px',
  fontWeight: 500,
});

const PlusIcon = styled(Box)({
  width: '24px',
  height: '24px',
});

// Main Component
export default function CategorySide({ branchId, user, setCategories, selectedCategoryId, setSelectedCategoryId }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [opens, setOpens] = useState({
    categoryModal: false,
    deleteModal: false,
    deleteCompleteModal: false,
    modalMode: 'add',
    activeCategory: 0,
    activeCategoryName: '',
  });

  // React Query로 카테고리 목록 조회
  const { data: cats = [] } = useQuery({
    queryKey: ['categories', branchId],
    queryFn: async () => {
      const data = await getBranchCategories(branchId, user.token);
      return data;
    },
    enabled: !!branchId && !!user?.token,
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 카테고리 삭제 mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: () => deletePointCategoryId(user.token, opens.activeCategory),
    onSuccess: () => {
      toast.success(t('Category deleted successfully'));
      queryClient.invalidateQueries({ queryKey: ['categories', branchId] });
      queryClient.invalidateQueries({ queryKey: ['products', branchId] });
      setOpens(prev => ({ ...prev, deleteModal: false, deleteCompleteModal: true }));
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 카테고리 목록이 변경되면 부모에 전달
  useEffect(() => {
    setCategories(cats);
  }, [cats, setCategories]);

  const handleCategoryClick = (categoryId, categoryName) => {
    // 락커 카테고리는 문자열 '락커'로 전달 (ProductSide에서 특수 처리)
    if (categoryName === '락커') {
      setSelectedCategoryId('락커');
    } else {
      setSelectedCategoryId(categoryId);
    }
  };

  const handleEditClick = (e, category) => {
    e.stopPropagation();
    setOpens({
      ...opens,
      categoryModal: true,
      modalMode: 'edit',
      activeCategory: category.categoryId,
      activeCategoryName: category.categoryName,
    });
  };

  const handleAddClick = () => {
    setOpens({ ...opens, categoryModal: true, modalMode: 'add', activeCategoryName: '' });
  };

  return (
    <Box>
      <CategoryListContainer>
        {/* 전체 카테고리 */}
        <CategoryItem isSelected={selectedCategoryId === null} onClick={() => handleCategoryClick(null)}>
          <CategoryName isSelected={selectedCategoryId === null}>{t('전체')}</CategoryName>
        </CategoryItem>

        {/* 카테고리 목록 */}
        {cats?.map(category => (
          <CategoryItem
            key={category.categoryId}
            isSelected={
              category.categoryName === '락커'
                ? selectedCategoryId === '락커'
                : selectedCategoryId === category.categoryId
            }
            onClick={() => handleCategoryClick(category.categoryId, category.categoryName)}>
            <CategoryName
              isSelected={
                category.categoryName === '락커'
                  ? selectedCategoryId === '락커'
                  : selectedCategoryId === category.categoryId
              }>
              {category.categoryName}
            </CategoryName>
            {category.default === false ? (
              <EditButton onClick={e => handleEditClick(e, category)}>{t('수정')}</EditButton>
            ) : (
              ''
            )}
          </CategoryItem>
        ))}

        {/* 분류 추가 버튼 */}
        <AddCategoryButton onClick={handleAddClick}>
          <PlusIcon component="svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            <path d="M12 8V16M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </PlusIcon>
          {t('분류 추가')}
        </AddCategoryButton>
      </CategoryListContainer>

      {/* 추가/수정 통합 모달 */}
      <CategoryEditModal
        open={opens.categoryModal}
        onClose={() => setOpens({ ...opens, categoryModal: false })}
        mode={opens.modalMode}
        branchId={branchId}
        user={user}
        activeCategoryId={opens.activeCategory}
        defaultCategoryName={opens.activeCategoryName}
        onDelete={() => setOpens({ ...opens, categoryModal: false, deleteModal: true })}
      />

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        open={opens.deleteModal}
        onClose={() => setOpens(prev => ({ ...prev, deleteModal: false }))}
        onConfirm={() => deleteCategoryMutation.mutate()}
        highlightText="삭제"
        highlightColor="#EE735A"
        confirmText="삭제"
        confirmColor="#EE735A"
      />

      {/* 삭제 완료 모달 */}
      <AlertModal
        open={opens.deleteCompleteModal}
        onClose={() => setOpens({ ...opens, deleteCompleteModal: false })}
        message="삭제가 완료되었습니다."
      />
    </Box>
  );
}
